import type { FC, FormEvent, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useId, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'received' | 'error'

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/

/**
 * Field Notes: one mono email field on a hairline with a small arrow button. On a successful
 * sign-up the line folds in to its centre and the field reads RECEIVED.
 */
export const FieldNotes: FC = (): ReactNode => {
    const router = useRouter()
    const inputId = useId()
    const [email, setEmail] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending' || status === 'received') return

        const value = email.trim()

        if (!EMAIL_PATTERN.test(value)) {
            setStatus('error')
            setMessage('Enter a valid email address.')

            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/field-notes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: value, page: router.asPath, website })
            })
            const data = await response.json().catch(() => ({})) as { ok?: boolean, message?: string }

            if (!response.ok || !data.ok) {
                setStatus('error')
                setMessage(data.message ?? 'The sign-up could not be saved. Please try again shortly.')

                return
            }

            setStatus('received')
        }
        catch {
            setStatus('error')
            setMessage('The sign-up could not be saved. Please try again shortly.')
        }
    }

    const received = status === 'received'

    return (
        <div>
            <p className='hud text-ink'>
                Field notes
            </p>

            <p className='mt-1 text-[14px] leading-[1.5] text-ink/75'>
                One report per quarter, no noise.
            </p>

            <form onSubmit={submit} noValidate className='relative mt-3 h-10'>
                <div className={cn('absolute inset-0 flex items-end transition-opacity duration-300 ease-instrument', received ? 'pointer-events-none opacity-0' : 'opacity-100')} aria-hidden={received || undefined}>
                    <label htmlFor={inputId} className='sr-only'>
                        Email for Field Notes
                    </label>

                    <input
                        id={inputId}
                        type='email'
                        name='email'
                        autoComplete='email'
                        inputMode='email'
                        placeholder='Email'
                        value={email}
                        onChange={event => {
                            setEmail(event.target.value)

                            if (status === 'error') setStatus('idle')
                        }}
                        disabled={status === 'sending' || received}
                        tabIndex={received ? -1 : 0}
                        aria-invalid={status === 'error' || undefined}
                        aria-describedby={message ? `${inputId}-message` : undefined}
                        className='hud h-9 min-w-0 flex-1 bg-transparent pb-1 text-ink placeholder:text-mute'
                    />

                    <input
                        type='text'
                        name='website'
                        value={website}
                        onChange={event => setWebsite(event.target.value)}
                        tabIndex={-1}
                        autoComplete='off'
                        aria-hidden='true'
                        className='absolute -left-[9999px] h-px w-px opacity-0'
                    />

                    <button
                        type='submit'
                        disabled={status === 'sending' || received}
                        tabIndex={received ? -1 : 0}
                        aria-label='Sign up for Field Notes'
                        className='mb-1 flex size-8 flex-none items-center justify-center border border-ink/40 text-ink transition-colors duration-300 hover:border-ink hover:text-signal disabled:opacity-60'
                    >
                        <ArrowRight className={cn('size-4', status === 'sending' && 'animate-pulse')} strokeWidth={1.75} aria-hidden='true' />
                    </button>
                </div>

                <span
                    aria-hidden='true'
                    className={cn(
                        'absolute inset-x-0 bottom-0 h-px origin-center transition-[transform,background-color] duration-500 ease-instrument',
                        status === 'error' ? 'bg-ink' : 'bg-ink/40',
                        received && 'scale-x-0'
                    )}
                />

                <p className={cn('hud absolute inset-x-0 bottom-2 text-ink transition-opacity duration-500 ease-instrument', received ? 'opacity-100 delay-300' : 'pointer-events-none opacity-0')} aria-hidden={!received || undefined}>
                    Received
                </p>
            </form>

            <p id={`${inputId}-message`} className='hud mt-2 min-h-4 text-mute' role='status' aria-live='polite'>
                {received ? 'Received. The next report comes at the end of the quarter.' : message}
            </p>
        </div>
    )
}
